import { useState, useRef, useEffect } from 'react';
import { Logo, Navbar, Row, Typography } from '@tempmee/react';
import { OutlineChevronDownIcon as ChevronDownIcon } from '@tempmee/react-icons';
import { colors, shadows } from '@tempmee/tailwind';
import { NavLink, useLocation } from 'react-router';
import { Links } from '~/constants';
import { fireLoginTabClicked, fireSignupTabClicked } from '~/posthogs/events/Signup';
import { useCancelPartialSession } from '../useCancelPartialSession';

const INACTIVE_COLOR = colors.shades[0];

const EXPLORE_ITEMS = [
  { label: 'Dental Office', href: Links.temporaryStaffing },
  { label: 'Dental Professional', href: Links.hygienist },
] as const;

const NAV_ITEM_PADDING = '18px 16px';

// TODO: replace with DS Dropdown component once available in @tempmee/react
const ExploreDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <Row as="span" align="center" style={{ padding: NAV_ITEM_PADDING, gap: 2 }}>
          <Typography as="span" color={INACTIVE_COLOR} style={{ fontSize: 18, whiteSpace: 'nowrap' }}>
            Explore GoTu
          </Typography>
          <ChevronDownIcon size={24} color={INACTIVE_COLOR} />
        </Row>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            backgroundColor: colors.shades[0],
            boxShadow: shadows['elevation-4'],
            borderRadius: 8,
            padding: '8px 0',
            minWidth: 180,
            zIndex: 10,
          }}
        >
          {EXPLORE_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noreferrer noopener"
              style={{ display: 'block', padding: '10px 16px', textDecoration: 'none' }}
            >
              <Typography as="span" size="body_large">{item.label}</Typography>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export interface IAuthHeaderNavProps {
  /**
   * Header chrome variant. `default` shows Log In + Sign Up nav items;
   * `forgot-password` hides those and surfaces "Forgot Password?" as the
   * active item. @default 'default'
   */
  variant?: 'default' | 'forgot-password';
}

export const AuthHeaderNav = ({ variant = 'default' }: IAuthHeaderNavProps = {}) => {
  const cancelPartialSession = useCancelPartialSession();
  const location = useLocation();
  const showPrimaryNav = variant === 'default';
  const showForgotPasswordLabel = variant === 'forgot-password';

  const handleLoginClick = () => {
    fireLoginTabClicked();
    cancelPartialSession();
  };

  const handleSignupClick = () => {
    fireSignupTabClicked();
  };

  return (
    <Navbar data-testid="auth-header-nav" style={{ boxShadow: shadows['elevation-1'] }}>
      <Navbar.Brand>
        <NavLink
          to="/login"
          aria-label="GoTu home"
          style={{ display: 'flex', alignItems: 'center' }}
          onClick={cancelPartialSession}
        >
          <Logo variant="light" height={44} />
        </NavLink>
      </Navbar.Brand>

      <Navbar.Items>
        {showPrimaryNav && (
          <Navbar.Item
            href="/login"
            active={location.pathname === '/login'}
            onClick={handleLoginClick}
          >
            Log In
          </Navbar.Item>
        )}

        {showPrimaryNav && (
          <Navbar.Item
            href="/signup"
            active={location.pathname === '/signup'}
            onClick={handleSignupClick}
          >
            Sign Up
          </Navbar.Item>
        )}

        {showForgotPasswordLabel && (
          <Navbar.Item active>Forgot Password?</Navbar.Item>
        )}

        <ExploreDropdown />
      </Navbar.Items>
    </Navbar>
  );
};
